import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import axiosExt from "../axios";
import { API_BASE_URL } from "../config";
import Layout from "../components/Layout";
import { getCreatedOnRange, CreatedOnFilter } from "../utils/helpers";

interface CustomerOrdersReport {
    customerId: string;
    name: string;
    current: number;
    previous: number;
    difference: number;
}

const ReportCustomerOrdersNoPage = () => {
    const { t } = useTranslation();

    const [rows, setRows] = useState<CustomerOrdersReport[]>([]);
    const [filterCreatedOn, setFilterCreatedOn] = useState<string>("this_month");
    const [customStart, setCustomStart] = useState<Dayjs | null>(null);
    const [customEnd, setCustomEnd] = useState<Dayjs | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");

            let startString: string;
            let endString: string;

            if (filterCreatedOn === "custom" && customStart && customEnd) {
                startString = customStart.startOf("day").format("YYYY-MM-DDTHH:mm:ss");
                endString = customEnd.endOf("day").format("YYYY-MM-DDTHH:mm:ss");
            } else {
                const createdOnRange = getCreatedOnRange(filterCreatedOn as CreatedOnFilter);
                if (createdOnRange) {
                    startString = createdOnRange.start.format("YYYY-MM-DDTHH:mm:ss");
                    endString = createdOnRange.end.format("YYYY-MM-DDTHH:mm:ss");
                } else {
                    // fallback: default to today
                    startString = dayjs().startOf("day").format("YYYY-MM-DDTHH:mm:ss");
                    endString = dayjs().endOf("day").format("YYYY-MM-DDTHH:mm:ss");
                }
            }

            const response = await axiosExt.get(`${API_BASE_URL}/customers/orders-number-report`, {
                params: { startDate: startString, endDate: endString },
                headers: { Authorization: `Bearer ${token}` },
            });

            const data = response.data.map((r: any) => ({
                ...r,
                customerId: r.id,
                id: r.id, // DataGrid key
            }));

            setRows(data);
        } catch (err) {
            console.error("Error fetching report:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, [filterCreatedOn, customStart, customEnd]);

    const columns: GridColDef[] = [
        { field: "name", headerName: t("Customer"), flex: 1 },
        { field: "current", headerName: t("Selected Period"), type: "number", width: 180 },
        { field: "previous", headerName: t("Previous Period"), type: "number", width: 180 },
        {
            field: "difference",
            headerName: t("Difference"),
            type: "number",
            width: 150,
            renderCell: (params) => (
                <span style={{ color: params.value >= 0 ? "green" : "red" }}>
                    {params.value}
                </span>
            ),
        },
    ];

    return (
        <Layout>
            <Box display="flex" width="100%" height="100vh">
                {/* Filters */}
                <Box width="20%" p={2}>
                    <Typography variant="h4">{t("Orders per Customer")}</Typography>

                    <FormControl fullWidth sx={{ marginTop: 2 }}>
                        <InputLabel>{t("Created On")}</InputLabel>
                        <Select
                            value={filterCreatedOn}
                            onChange={(e) => setFilterCreatedOn(e.target.value)}
                        >
                            <MenuItem value="today">{t("Today")}</MenuItem>
                            <MenuItem value="this_week">{t("This Week")}</MenuItem>
                            <MenuItem value="this_month">{t("This Month")}</MenuItem>
                            <MenuItem value="last_month">{t("Last Month")}</MenuItem>
                            <MenuItem value="last_30_days">{t("Last 30 Days")}</MenuItem>
                            <MenuItem value="custom">{t("Custom Range")}</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Show date pickers if custom range is selected */}
                    {filterCreatedOn === "custom" && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label={t("Start Date")}
                                value={customStart}
                                onChange={(date) => setCustomStart(date)}
                                slotProps={{ textField: { fullWidth: true, sx: { mt: 2 } } }}
                            />
                            <DatePicker
                                label={t("End Date")}
                                value={customEnd}
                                onChange={(date) => setCustomEnd(date)}
                                slotProps={{ textField: { fullWidth: true, sx: { mt: 2 } } }}
                            />
                        </LocalizationProvider>
                    )}

                    <Box mt={2}>
                        <Button variant="contained" onClick={fetchReport}>
                            {t("Apply Filters")}
                        </Button>
                    </Box>
                </Box>

                {/* Data Table */}
                <Box width="80%" p={2}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSizeOptions={[25, 50, 100]}
                        disableRowSelectionOnClick
                        autoHeight
                        loading={loading}
                    />

                    {/* Optional full-screen loader if needed */}
                    {loading && rows.length === 0 && (
                        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                            <CircularProgress />
                        </Box>
                    )}

                    {/* No data placeholder */}
                    {!loading && rows.length === 0 && (
                        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                            <Typography variant="body1">{t("No data available for this period")}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Layout>
    );
};

export default ReportCustomerOrdersNoPage;
